import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import type { DispensarizationBatch } from '../../models/MedVox/DispensarizationBatch';
import type { Status } from '../../models/common/Status';
import type {
  FormTemplate,
  FormTemplatesResponse,
} from '../../models/TaskForm/TaskForm';
import {
  outgoingCallsUploadApi,
  type UploadFormData,
  type UploadResult,
} from '../../services/outgoingCallsUploadApi';
import { triggerBlobDownload } from '../../utils/http/responseHelpers';
import type { RootState } from '../store';

export type {
  UploadFormData,
  UploadResult,
} from '../../services/outgoingCallsUploadApi';
export { normalizeUploadResult } from '../../services/outgoingCallsUploadApi';

export interface OutgoingCallsUploadState {
  dispensarizationBatches: DispensarizationBatch[];
  outboundTaskTypes: string[];
  outboundTaskTypeLabels: Record<string, string>;
  formTemplates: FormTemplate[];
  templateLoading: boolean;
  status: Status;
}

const initialState: OutgoingCallsUploadState = {
  dispensarizationBatches: [],
  outboundTaskTypes: [],
  outboundTaskTypeLabels: {},
  formTemplates: [],
  templateLoading: false,
  status: {
    responseLoading: true,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};

/**
 * Fetches outbound task types from GET /api/task/type/outbound.
 * Maps response to TaskType keys with localized labels.
 */
export const getOutboundTaskTypes = createAsyncThunk<
  { types: string[]; labels: Record<string, string> },
  void,
  { rejectValue: string }
>(
  'OutgoingCallsUpload/getOutboundTaskTypes',
  async (_, { rejectWithValue }) => {
    try {
      return await outgoingCallsUploadApi.getOutboundTaskTypes();
    } catch (error) {
      return rejectWithValue(
        axios.isAxiosError(error)
          ? error.message
          : 'Не удалось получить типы заданий!',
      );
    }
  },
);

/**
 * Fetches form templates for the form upload scenario.
 * Backend requires `sortQuery` even though Swagger does not mark it as required.
 */
export const getOutboundFormTemplates = createAsyncThunk<
  FormTemplatesResponse,
  void,
  { rejectValue: string }
>('OutgoingCallsUpload/getFormTemplates', async (_, { rejectWithValue }) => {
  try {
    return await outgoingCallsUploadApi.getOutboundFormTemplates();
  } catch (error) {
    return rejectWithValue(
      axios.isAxiosError(error)
        ? error.message
        : 'Не удалось получить шаблоны анкет!',
    );
  }
});

export const uploadOutgoingCalls = createAsyncThunk<
  UploadResult,
  { formData: UploadFormData; taskType: string }
>(
  'OutgoingCallsUpload/uploadOutgoingCalls',
  async ({ formData, taskType }, { rejectWithValue }) => {
    try {
      const result = await outgoingCallsUploadApi.upload(formData, taskType);
      if (result) {
        return result;
      }

      // No API contract for this scenario group — this is a backend dependency
      return rejectWithValue('NO_CONTRACT');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Не удалось загрузить список!');
    }
  },
);

export const downloadTemplate = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  'OutgoingCallsUpload/downloadTemplate',
  async (taskType, { rejectWithValue }) => {
    try {
      const template = await outgoingCallsUploadApi.getTemplate(taskType);
      if (!template) {
        // No API contract for this scenario group — this is a backend dependency
        return rejectWithValue('NO_CONTRACT');
      }

      triggerBlobDownload(template.blob, template.fileName);
      return template.fileName;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Не удалось загрузить шаблон!');
    }
  },
);

export const outgoingCallsUploadSlice = createSlice({
  name: 'outgoingCallsUpload',
  initialState,
  reducers: {
    clearOutboundTaskTypes(state) {
      state.outboundTaskTypes = [];
      state.outboundTaskTypeLabels = {};
    },
  },
  extraReducers(builder) {
    builder
      // getOutboundTaskTypes
      .addCase(getOutboundTaskTypes.fulfilled, (state, action) => {
        state.outboundTaskTypes = action.payload.types;
        state.outboundTaskTypeLabels = action.payload.labels;
      })

      // getOutboundFormTemplates
      .addCase(getOutboundFormTemplates.fulfilled, (state, action) => {
        state.formTemplates = action.payload.items ?? [];
      })

      // uploadOutgoingCalls
      .addCase(uploadOutgoingCalls.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        return state;
      })
      .addCase(uploadOutgoingCalls.fulfilled, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        return state;
      })
      .addCase(uploadOutgoingCalls.rejected, (state, action) => {
        state.status.responseLoading = false;
        if (action.payload === 'NO_CONTRACT') {
          state.status.responseError = 'NO_CONTRACT';
        } else {
          state.status.responseError = 'Не удалось загрузить файл';
        }
      })

      // downloadTemplate
      .addCase(downloadTemplate.pending, (state) => {
        state.templateLoading = true;
        state.status.responseLoading = true;
        state.status.responseError = null;
        return state;
      })
      .addCase(downloadTemplate.fulfilled, (state) => {
        state.templateLoading = false;
        state.status.responseLoading = false;
        state.status.responseError = null;
        return state;
      })
      .addCase(downloadTemplate.rejected, (state, action) => {
        state.templateLoading = false;
        state.status.responseLoading = false;
        if (action.payload === 'NO_CONTRACT') {
          state.status.responseError = 'NO_CONTRACT';
        } else {
          state.status.responseError = 'Не удалось скачать шаблон';
        }
        return state;
      });
  },
});

export const { clearOutboundTaskTypes } = outgoingCallsUploadSlice.actions;

export default outgoingCallsUploadSlice.reducer;

// Selectors
export const selectOutboundTaskTypes = (state: RootState) =>
  state.outgoingCallsUpload.outboundTaskTypes;

export const selectOutboundTaskTypeLabels = (state: RootState) =>
  state.outgoingCallsUpload.outboundTaskTypeLabels;

export const selectOutboundFormTemplates = (state: RootState) =>
  state.outgoingCallsUpload.formTemplates;

export const selectUploadStatus = (state: RootState) =>
  state.outgoingCallsUpload.status;

export const selectTemplateLoading = (state: RootState) =>
  state.outgoingCallsUpload.templateLoading;
